import { createEvidence, withAlbumState, type V2Evidence } from '../domain/evidence';
import { probeImage } from '../native/imageTools';
import { getMediaAdapter } from '../native/mediaAdapter';
import type { AlbumSaveResult, CapturedFile } from '../native/mediaContract';
import { deleteEvidenceWithMedia, putEvidenceWithMedia, putRecords, type RecordWrite } from '../persistence/v2Database';
import { removeEvidenceLocal, upsertEvidenceLocal } from './evidenceStore';
import { prepareFindingEvidenceLink, prepareFindingEvidenceUnlinks } from './findingStore';
import { assertInspectionUnlocked, prepareInspectionTouch, prepareItemEvidenceLink, prepareItemEvidenceUnlinks } from './inspectionStore';
import { evidenceStore } from './evidenceStore';
import { releaseMediaUrls } from './mediaUrls';

// The evidence lane, in the order the owner contract dictates (src/native/mediaContract.ts):
//
//   camera capture succeeds
//     → (1) system-album save is REQUESTED — first thing, before anything below
//     → (2) evidence record + bytes + links persisted in one transaction
//     → (3) the album outcome is written onto the photo when it arrives (never silent)
//
// Library imports skip (1) entirely: the original is already in the album and is never
// re-saved (provenance fail-closed — only the app's own camera lane is 'camera').
// Removing a photo from the app touches this database only; there is no album API to call.
export interface EvidenceTarget {
  inspectionId: string;
  // Capture location area; null = the inspection's photo pool (bulk import — Owner task book §8 / §9).
  areaId: string | null;
  // Capture location item (null at area level). Also linked as evidence when set.
  itemId: string | null;
  // Existing finding to link to (null for area/item captures or an unsaved finding editor).
  findingId: string | null;
  // 2026-10-19 (Owner item 31): the photo-checklist shot this capture answers (src/domain/photoGuide.ts).
  shot?: string | null;
}

export type CaptureOutcome =
  | { kind: 'cancelled' }
  | { kind: 'saved'; evidence: V2Evidence }
  | { kind: 'error'; message: string };

export type ImportOutcome =
  | { kind: 'cancelled' }
  | { kind: 'saved'; evidence: V2Evidence[] }
  | { kind: 'error'; message: string };

// Owner device test 2026-10-05: an import of many photos takes a while on the phone (every photo is
// decoded, thumbnailed and written one after the other), and with nothing moving the inspector thought
// the app had frozen. The lane reports (0, total) as soon as the picked files arrive and (n, total)
// after each photo is safely stored, so the screen can show a real progress bar and percentage.
export type ImportProgressListener = (done: number, total: number) => void;

function messageOf(raw: unknown): string {
  return raw instanceof Error ? raw.message : String(raw);
}

function buildLinks(evidenceId: string, target: EvidenceTarget, now: number): { writes: RecordWrite[]; apply: () => void } {
  const parts = [
    target.itemId ? prepareItemEvidenceLink(target.itemId, evidenceId, now) : null,
    target.findingId ? prepareFindingEvidenceLink(target.findingId, evidenceId, now) : null,
    prepareInspectionTouch(target.inspectionId, now)
  ];
  return {
    writes: parts.flatMap((p) => (p && p.write ? [p.write] : [])),
    apply: () => {
      for (const p of parts) p?.apply();
    }
  };
}

async function persistNewEvidence(file: CapturedFile, evidence: V2Evidence, target: EvidenceTarget, now: number): Promise<V2Evidence> {
  const probe = await probeImage(file.blob);
  const sized: V2Evidence = { ...evidence, width: probe.width, height: probe.height };
  const links = buildLinks(sized.id, target, now);
  const fullBytes = await file.blob.arrayBuffer();
  const thumbBytes = probe.thumbnail ? await probe.thumbnail.arrayBuffer() : null;
  await putEvidenceWithMedia(
    {
      evidenceId: sized.id,
      full: { type: file.mimeType, bytes: fullBytes },
      thumbnail: thumbBytes ? { type: 'image/jpeg', bytes: thumbBytes } : null
    },
    [{ store: 'evidence', value: sized }, ...links.writes]
  );
  upsertEvidenceLocal(sized);
  links.apply();
  return sized;
}

export async function captureFromCameraAction(target: EvidenceTarget, now: number = Date.now()): Promise<CaptureOutcome> {
  assertInspectionUnlocked(target.inspectionId);
  const adapter = getMediaAdapter();
  let file: CapturedFile | null;
  try {
    file = await adapter.captureFromCamera();
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  }
  if (!file) return { kind: 'cancelled' };

  // (1) ALBUM FIRST. Nothing below runs before this call has been made.
  const albumPromise: Promise<AlbumSaveResult> = adapter
    .saveToSystemAlbum(file)
    .catch((raw) => ({ state: 'failed' as const, detail: `相册保存失败：${messageOf(raw)}` }));

  // (2) Persist + link.
  const evidence = createEvidence(
    {
      inspectionId: target.inspectionId,
      areaId: target.areaId,
      itemId: target.itemId,
      source: 'camera',
      mimeType: file.mimeType,
      byteSize: file.blob.size,
      width: null,
      height: null,
      shot: target.shot ?? null
    },
    now
  );
  let stored: V2Evidence;
  try {
    stored = await persistNewEvidence(file, evidence, target, now);
  } catch (raw) {
    return { kind: 'error', message: `照片保存到 App 失败：${messageOf(raw)}` };
  }

  // (3) Album outcome onto the photo — observable either way.
  const result = await albumPromise;
  const settled = withAlbumState(stored, result.state, result.detail, Date.now());
  try {
    await putRecords([{ store: 'evidence', value: settled }]);
    upsertEvidenceLocal(settled);
  } catch {
    // The photo itself is safe in the app; the ledger update failing is reported via the
    // in-memory record so the UI still shows the real album outcome.
    upsertEvidenceLocal(settled);
  }
  return { kind: 'saved', evidence: settled };
}

export async function importFromLibraryAction(target: EvidenceTarget, now: number = Date.now(), onProgress?: ImportProgressListener): Promise<ImportOutcome> {
  assertInspectionUnlocked(target.inspectionId);
  const adapter = getMediaAdapter();
  let files: CapturedFile[];
  try {
    files = await adapter.pickFromLibrary();
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  }
  if (files.length === 0) return { kind: 'cancelled' };
  onProgress?.(0, files.length);
  const saved: V2Evidence[] = [];
  for (const [index, file] of files.entries()) {
    // Imported photos are never re-saved to the album: no saveToSystemAlbum call on this path.
    const evidence = createEvidence(
      {
        inspectionId: target.inspectionId,
        areaId: target.areaId,
        itemId: target.itemId,
        source: 'photoLibrary',
        mimeType: file.mimeType,
        byteSize: file.blob.size,
        width: null,
        height: null,
        shot: target.shot ?? null
      },
      now + index
    );
    try {
      saved.push(await persistNewEvidence(file, evidence, target, now + index));
    } catch (raw) {
      if (saved.length === 0) return { kind: 'error', message: `照片保存到 App 失败：${messageOf(raw)}` };
      break;
    }
    onProgress?.(index + 1, files.length);
  }
  return { kind: 'saved', evidence: saved };
}

// Bulk import into the inspection's PHOTO POOL (Path A, task book §8): every picked photo becomes an
// evidence record of the inspection with NO capture area and no links — the pool. Nothing here
// depends on AI. The library lane never re-saves to the album (provenance fail-closed).
export function importToPoolAction(inspectionId: string, now: number = Date.now(), onProgress?: ImportProgressListener): Promise<ImportOutcome> {
  return importFromLibraryAction({ inspectionId, areaId: null, itemId: null, findingId: null }, now, onProgress);
}

// Link an existing photo (an area photo or a pool photo) to an item or finding. Capture location on
// the evidence record is untouched — this only adds an evidence link; the photo stays in the pool.
export async function linkExistingEvidenceAction(
  evidenceId: string,
  target: { inspectionId: string; itemId: string | null; findingId: string | null },
  now: number = Date.now()
): Promise<void> {
  assertInspectionUnlocked(target.inspectionId);
  const links = buildLinks(evidenceId, { inspectionId: target.inspectionId, areaId: null, itemId: target.itemId, findingId: target.findingId }, now);
  await putRecords(links.writes);
  links.apply();
}

// Removes the app's record + bytes and every link to it. Deliberately has no access to the
// media adapter: the system album copy is never touched (OWNER-CONTRACT §6.1.2).
export async function removeEvidenceRecordAction(evidenceId: string, now: number = Date.now()): Promise<void> {
  const record = evidenceStore.get().evidence.find((e) => e.id === evidenceId);
  if (record) assertInspectionUnlocked(record.inspectionId);
  const itemUnlinks = prepareItemEvidenceUnlinks(evidenceId, now);
  const findingUnlinks = prepareFindingEvidenceUnlinks(evidenceId, now);
  await deleteEvidenceWithMedia(evidenceId, [...itemUnlinks.writes, ...findingUnlinks.writes]);
  itemUnlinks.apply();
  findingUnlinks.apply();
  removeEvidenceLocal(evidenceId);
  releaseMediaUrls([evidenceId]);
}
